import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaDevice, Constants } from "@videosdk.live/react-sdk";

/**
 * Camera / microphone / location permission tracking for the pre-call checklist.
 *
 * Camera & microphone go through the VideoSDK useMediaDevice() APIs:
 *   - requestPermission() fires the native prompt once, when the modal opens
 *   - checkPermissions() reads the granted status
 * Location has no SDK equivalent, so its granted status is read from
 * navigator.permissions (the location prompt itself is fired from the Intro
 * page's on-mount check).
 *
 * Each row is binary: granted (green "Access granted") or not. A not-granted
 * row shows "How to enable" (manual guidance) — there is no retry action.
 *
 * Status is LIVE: re-checked on window focus, tab visibility, and browser
 * permission changes, so revoking mid-flow downgrades a row instead of going
 * stale.
 *
 * @param {boolean} active - whether the checklist is visible (gates tracking).
 */
export default function usePermissionChecklist(active) {
  const { checkPermissions, requestPermission } = useMediaDevice();

  // Stabilize the SDK methods behind refs so effects don't re-run if
  // useMediaDevice returns fresh function references each render.
  const checkPermissionsRef = useRef(checkPermissions);
  checkPermissionsRef.current = checkPermissions;
  const requestPermissionRef = useRef(requestPermission);
  requestPermissionRef.current = requestPermission;

  // Fire the camera+mic prompt only once per page load.
  const requestedRef = useRef(false);

  // Whether the location prompt has been fired AND resolved (granted, denied,
  // or dismissed). Once true, a lingering "prompt" permission state means the
  // user dismissed the prompt (tapped outside) rather than an in-flight ask, so
  // it must degrade to "How to enable" instead of spinning "Checking" forever.
  const locationAskedRef = useRef(false);

  // Granted status per row (true | false | null while first checking).
  const [perm, setPerm] = useState({
    camera: null,
    microphone: null,
    location: null,
  });
  const [helpOpen, setHelpOpen] = useState(false);

  // Read camera/mic via VideoSDK checkPermissions; location via
  // navigator.permissions (no SDK equivalent for geolocation).
  const refresh = useCallback(async () => {
    try {
      const map = await checkPermissionsRef.current(
        Constants.permission.AUDIO_AND_VIDEO,
      );
      setPerm((prev) => ({
        ...prev,
        camera: !!map.get(Constants.permission.VIDEO),
        microphone: !!map.get(Constants.permission.AUDIO),
      }));
    } catch (e) {
      // leave as-is
    }

    // Location is tri-state so a still-pending prompt shows "Checking" instead
    // of a premature "How to enable": granted -> true, denied -> false
    // (guidance), prompt -> null (still being asked, spinner).
    //
    // But "prompt" is overloaded: it also lingers after the user DISMISSES the
    // prompt (taps outside) without deciding. Once we've fired and resolved the
    // location prompt (locationAskedRef), a "prompt" state can only mean
    // "dismissed", so map it to false ("How to enable") instead of null — else
    // the row spins "Checking" forever.
    let location = false;
    try {
      const state = (await navigator.permissions.query({ name: "geolocation" }))
        .state;
      location =
        state === "granted"
          ? true
          : state === "prompt"
            ? locationAskedRef.current
              ? false
              : null
            : false;
    } catch (e) {
      // Browser can't report it (e.g. Safari) — fall back to manual guidance.
    }
    setPerm((prev) => ({ ...prev, location }));
  }, []);

  const openHelp = useCallback(() => setHelpOpen(true), []);
  const closeHelp = useCallback(() => {
    setHelpOpen(false);
    refresh();
  }, [refresh]);

  // Reset the help dialog when the checklist closes so it never re-opens stale.
  useEffect(() => {
    if (!active) setHelpOpen(false);
  }, [active]);

  // Live tracking. On the FIRST open, fire the VideoSDK camera+mic prompt via
  // requestPermission() and only read status once it resolves — so a row shows
  // "Checking" while the prompt is up, not a premature "How to enable". On later
  // opens the request is skipped (asked once) and we just read. Focus /
  // visibility / permission-change events re-read so revocations show live.
  useEffect(() => {
    if (!active) return undefined;
    let mounted = true;

    (async () => {
      if (!requestedRef.current) {
        requestedRef.current = true;
        try {
          await requestPermissionRef.current(
            Constants.permission.AUDIO_AND_VIDEO,
          );
        } catch (e) {
          // Denied / dismissed — refresh() below reflects it as not granted.
        }
        // Fire the location prompt only AFTER the camera/mic prompt resolves, so
        // only ONE permission prompt is ever outstanding. Chrome embargoes a
        // geolocation request that overlaps another prompt ("Chrome thinks you
        // don't want to use your location on this site"). getCurrentPosition is
        // a no-op prompt when location is already granted/denied.
        if (navigator.geolocation) {
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              () => {
                locationAskedRef.current = true;
                if (mounted) setPerm((prev) => ({ ...prev, location: true }));
                resolve();
              },
              () => {
                // Denied OR dismissed (tapped outside). Either way the prompt is
                // resolved; refresh() below now degrades a lingering "prompt"
                // state to "How to enable" instead of an endless "Checking".
                locationAskedRef.current = true;
                resolve();
              },
              { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 },
            );
          });
        }
      }
      if (mounted) refresh();
    })();

    const onFocusOrVisible = () => {
      if (document.visibilityState !== "hidden") refresh();
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);

    // navigator.permissions onchange → re-read instantly when the browser
    // permission flips (e.g. revoked from the address bar).
    const statuses = [];
    const watch = async (name) => {
      try {
        const status = await navigator.permissions.query({ name });
        if (!mounted) return;
        statuses.push(status);
        status.onchange = refresh;
      } catch (e) {
        // Not supported — rely on the focus / visibility re-check.
      }
    };
    watch("camera");
    watch("microphone");
    watch("geolocation");

    return () => {
      mounted = false;
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
      statuses.forEach((s) => {
        s.onchange = null;
      });
    };
  }, [active, refresh]);

  return {
    perm,
    helpOpen,
    openHelp,
    closeHelp,
    // Camera + microphone + location are hard requirements for "Continue".
    allGranted: Boolean(perm.camera && perm.microphone && perm.location),
  };
}
